<template>
  <div class="webeo-wrapper">
    <div class="webeo-container">
      <div class="webeo-header">
        <img
          src="../assets/webeo-logo.png"
          alt="Webeo company logo"
        >
      </div>

      <div
        v-if="!complete"
        class="reset-content"
      >
        <el-form
          v-if="!success"
          ref="passwordForm"
          class="reset-form"
          :hide-required-asterisk="true"
          :model="form"
          :rules="passwordFormRules"
        >
          <el-form-item
            class="reset-form-item"
            prop="newPassword"
          >
            <div slot="label">
              <div class="reset-form-label">
                <span>{{ $t('resetPassword.newPassword.label') }}</span>
                <WebeoInfo>{{ $t('resetPassword.newPassword.info') }}</WebeoInfo>
              </div>

              <ul class="reset-form-help">
                <li>
                  {{ $t('resetPassword.newPassword.helpPoint1') }}
                </li>
                <li>
                  {{ $t('resetPassword.newPassword.helpPoint2') }}
                </li>
                <li>
                  {{ $t('resetPassword.newPassword.helpPoint3') }}
                </li>
              </ul>
            </div>
            <PortalInput
              v-model="form.newPassword"
              :placeholder="$t('resetPassword.newPassword.placeHolder')"
              data-test="reset_new_password"
              :type="getPasswordFieldType(form.showNewPassword)"
              :suffix-clickable="true"
              @input="$v.form.newPassword.$touch()"
              @suffix-clicked="form.showNewPassword = !form.showNewPassword"
            >
              <font-awesome-icon
                :icon="getPasswordIcon(form.showNewPassword)"
                data-test="reveal-new-password"
              />
            </PortalInput>
            <password
              v-model="form.newPassword"
              :show-strength-meter="showStrengthMeter"
              :strength-meter-only="true"
              :secure-length="25"
              @score="getPasswordScore"
            />
          </el-form-item>

          <el-form-item
            class="form-item"
            prop="confirmNewPassword"
          >
            <div
              slot="label"
              class="reset-form-label"
            >
              <span>{{ $t('resetPassword.confirmPassword.label') }}</span>
              <WebeoInfo>{{ $t('resetPassword.confirmPassword.info') }}</WebeoInfo>
            </div>
            <PortalInput
              v-model="form.confirmNewPassword"
              :placeholder="$t('resetPassword.confirmPassword.placeHolder')"
              data-test="reset_new_password"
              :type="getPasswordFieldType(form.showConfirmPassword)"
              :suffix-clickable="true"
              @input="$v.form.confirmNewPassword.$touch()"
              @suffix-clicked="form.showConfirmPassword = !form.showConfirmPassword"
            >
              <font-awesome-icon
                :icon="getPasswordIcon(form.showConfirmPassword)"
                data-test="reveal-confirm-password"
              />
            </PortalInput>
          </el-form-item>
        </el-form>

        <div
          v-if="message"
          class="forgotten-password-error"
        >
          {{ message }}
        </div>

        <div class="reset-password-actions">
          <button
            data-test="reset-password"
            @click="resetPasswordSubmit()"
          >
            {{ $t('resetPassword.updateButton') }}
          </button>
        </div>
      </div>

      <div
        v-if="complete"
        class="reset-complete"
      >
        <div class="forgotten-password-error">
          {{ message }}
        </div>

        <div
          class="reset-password-actions"
          :class="[success && 'success']"
        >
          <button
            class="return-button"
            data-test="reset-password"
            @click="returnToLogin()"
          >
            {{ $t('resetPassword.returnToLogin') }}
          </button>
        </div>
      </div>
    </div>

    <SupportDetails />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import Password from 'vue-password-strength-meter';
import SupportDetails from '../components/SupportDetails.vue';
import PortalInput from '../components/PortalInput.vue';

export default {
  components: {
    Password,
    SupportDetails,
    PortalInput,
  },
  data: () => ({
    form: {
      newPassword: '',
      showNewPassword: false,
      confirmNewPassword: '',
      showConfirmPassword: false,
    },
    showStrengthMeter: false,
    passwordScore: null,
    message: '',
    complete: false,
    expired: false,
    success: false,
    passwordRequiredLength: 9,
  }),
  validations: {
    form: {
      newPassword: {
        required,
      },
      confirmNewPassword: {
        required,
      },
    },
  },
  computed: {
    passwordFormRules() {
      return {
        newPassword: [
          { type: 'string', required: true, message: this.$t('resetPassword.newPassword.requiredValidation') },
          { validator: this.passwordFormValidator },
        ],
        confirmNewPassword: [
          { type: 'string', required: true, message: this.$t('resetPassword.confirmPassword.requiredValidation') },
          { validator: this.passwordConfirmValidator },
        ],
      };
    },
  },
  watch: {
    form: {
      handler() {
        this.$refs.passwordForm.validateField('newPassword');
        this.showStrengthMeter = this.isPasswordValid();
      },
      deep: true,
    },
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    this.form.token = urlParams.get('token');
    this.form.username = urlParams.get('username');
    const strongPasswordRequired = urlParams.get('st') === 'true';
    this.passwordRequiredLength = this.getRequiredPasswordLength(strongPasswordRequired);
  },
  methods: {
    ...mapActions([
      'notify',
      'resetPassword',
    ]),
    passwordFormValidator(rule, value, callback) {
      if (this.form.newPassword.length < this.passwordRequiredLength) {
        callback(
          new Error(`${this.$t('resetPassword.newPassword.minLengthValidation')}${this.passwordRequiredLength}`),
        );
        return;
      }

      if (this.form.newPassword.length > 50) {
        callback(new Error(`${this.$t('resetPassword.newPassword.maxLengthValidation')}`));
        return;
      }

      if (this.passwordScore < 3) {
        callback(new Error(this.$t('resetPassword.newPassword.complexityValidation')));
        return;
      }

      callback();
    },
    isPasswordValid() {
      return this.passwordScore >= 3
          && this.form.newPassword.length >= this.passwordRequiredLength;
    },
    getRequiredPasswordLength(strongPasswordRequired) {
      return strongPasswordRequired ? 25 : 9;
    },
    getPasswordScore(score) {
      this.passwordScore = score;
    },
    passwordConfirmValidator(rule, value, callback) {
      if (value !== this.form.newPassword) {
        callback(new Error(this.$t('resetPassword.confirmPassword.matchValidation')));
        return;
      }

      callback();
    },
    async resetPasswordSubmit() {
      const formValid = await this.$refs.passwordForm.validate();
      if (!formValid) { return; }
      const updatePasswordResult = await this.resetPassword(this.form);

      if (updatePasswordResult.passwordNotStrongEnough) {
        this.notify({ title: 'Error', message: updatePasswordResult.message });
        return;
      }

      this.complete = true;

      if (updatePasswordResult.success) {
        this.notify({ title: 'Success', message: this.$t('resetPassword.updateSuccessMessage') });
        this.message = this.$t('resetPassword.updateSuccessMessage');
        this.success = true;
        return;
      }

      this.notify({ title: 'Error', message: updatePasswordResult.message });
      this.message = this.$t('resetPassword.updateErrorMessage');
      this.success = false;
    },
    returnToLogin() {
      this.$router.push('/Login');
    },
    getPasswordIcon(show) {
      return show ? 'eye' : 'eye-slash';
    },
    getPasswordFieldType(show) {
      return show ? 'text' : 'password';
    },
  },
};
</script>

<style scoped>
.webeo-wrapper {
  position: relative;
  top: 5%;
}
.webeo-container {
  width: 650px;
  border: 1px solid var(--webeo-grey100);
  margin: 0 auto;
  background-color: var(--webeo-white);
}
.dark-theme .webeo-container {
  border: 1px solid var(--webeo-white);
  background-color: var(--webeo-background-2);
}
.webeo-header {
  padding: 30px 0px 20px 0px;
  display: flex;
  justify-content: center;
}
.webeo-header img {
  width: 200px;
  margin-right: 70px;
}
.profile-form-description {
  margin-top: 30px;
  margin-bottom: 52px;
  font-size: 15px;
  color: var(--webeo-black);
}
.reset-content, .reset-complete {
  display: flex;
  flex-direction: column;
  padding: 0 75px 20px 75px;
}
.reset-form-label {
  display: flex;
  flex-direction: row;
  position: relative;
  z-index: 1;
}
.reset-form-label > span {
  margin-right: 8px;
}
ul.reset-form-help {
  margin: 0 0 18px 0;
  text-align: start;
}
ul.reset-form-help > li {
  margin: 0;
  padding: 0;
  line-height: 20px;
}
.reset-password-actions button {
  width: 100%;
  height: 40px;
  border-radius: 5px;
  margin-top: 30px;
  border: none;
  background-color: var(--webeo-magenta);
  color: var(--webeo-white);
  text-transform: uppercase;
  font-size: 16px;
  cursor: pointer;
}
.reset-password-actions button + button:hover {
  background-color: var(--webeo-grey150);
  border: 1px solid var(--webeo-grey170);
}
.reset-password-actions button:hover {
  background-color: var(--webeo-magenta-hover);
}
.reset-password-actions button:focus {
  outline: none;
}
.forgotten-password-error {
  background-color: var(--webeo-purple);
  color: var(--webeo-white);
  margin-top: 20px;
  text-align: justify;
  padding: 12px;
  font-size: 13px;
  border-radius: 5px;
}
.forgotten-password-error.success {
  background-color: var(--webeo-secondary);
}
.reset-password-actions button.return-button {
  border: 1px solid var(--webeo-grey100);
  color: var(--webeo-black);
}
.reset-password-actions button.return-button:hover {
  background-color: var(--webeo-grey150);
  border: 1px solid var(--webeo-grey170);
}
.reset-password-actions button.return-button:focus {
  outline: none;
}
</style>

<style>
.reset-content .Password__strength-meter {
  margin-bottom: 10px !important;
  margin-top: 20px !important;
}
.reset-content input, .reset-content select {
  max-width: unset !important;
}
.reset-content .el-form-item {
  margin-bottom: 0px !important;
}
.reset-content .Password + .el-form-item__error {
  top: 232px !important;
}
</style>
